import { ReactNode, useState } from "react"
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faComment, faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useDateTime } from "../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { MeetingDetailsOrder } from "../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import { STATUS_CHIP_VARIANT, STATUS_MAP } from "../../../modules/merchant/merchant/production/meetingService/constants"
import { getContactInfo } from "../../../modules/merchant/merchant/production/meetingService/components/slipModal/utils"
import { HostContactDetailsDialog } from "../../../modules/serviceCenter/report/home/components/drawer/HostContactDetailsDialog"
import { Organizer } from "../../types/OrganizerTypes"
import { getOrganizerPhone } from "../../utils/getOrderPerson"
import { StandardChip } from "../badges/StandardChip"
import { DrawerDetailRow } from "../drawers/DrawerDetailRow"
import { DrawerDetailsCard } from "../drawers/DrawerDetailsCard"
import { ExpandableText } from "../text/ExpandableText"

const useStyles = makeStyles()((theme) => ({
    detailValue: {
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.tailwind.slate[800],
        textAlign: "right",
    },
    contactInfoButton: {
        padding: 0,
        color: theme.palette.tailwind.slate[500],
    },
    noteCallout: {
        marginTop: "1rem",
        padding: ".75rem 1rem",
        borderRadius: ".75rem",
        background: theme.palette.tailwind.amber[50],
    },
    noteTitle: {
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.tailwind.yellow[700],
        textTransform: "uppercase",
    },
    noteText: {
        color: theme.palette.tailwind.stone[700],
    },
}))

type Props = Readonly<{
    details: MeetingDetailsOrder
    isMerchantView: boolean
}>

export function OrderDetailsCard({ details, isMerchantView }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const [isContactDialogOpen, setIsContactDialogOpen] = useState(false)

    const sittings = details.deliveryDetails.sittings ?? 0
    const customerContact: Organizer | undefined = details.customer && {
        name: details.customer.displayName,
        email: details.customer.email,
    }
    const contact = isMerchantView
        ? (getContactInfo(details.organizers, details.customer).meetingHost ?? customerContact)
        : customerContact
    const contactName = contact?.name ?? ""
    const hasContactInfo = Boolean(contact?.email || getOrganizerPhone(contact))
    const deliveryType = details.deliveryDetails.type
    const deliveryTypeLabel = deliveryType
        ? l10n.getStringForEnum("delivery-type-filter", deliveryType, deliveryType)
        : ""
    const locationName = details.deliveryDetails.location?.name ?? ""
    const orderStatus = details.deliveryDetails.statusDetails?.status
    const deliveryDateTime = dateTimeFactory.fromApi(details.deliveryDetails.time ?? "")
    const deliveryTimeLabel = deliveryDateTime.isValid
        ? `${dateTimeFactory.formatDayAndDate(deliveryDateTime)} · ${dateTimeFactory.formatTime(deliveryDateTime)}`
        : "-"

    const detailRows: ReadonlyArray<{
        key: string
        label: ReactNode
        value: ReactNode
        isElement?: boolean
    }> = [
        ...(orderStatus
            ? [
                  {
                      key: "status",
                      label: (
                          <LocalizedStrict id="meeting-service-order-drawer-status">
                              <span>Status</span>
                          </LocalizedStrict>
                      ),
                      value: (
                          <LocalizedStrict id={STATUS_MAP[orderStatus].stateTranslationId}>
                              <StandardChip variant={STATUS_CHIP_VARIANT[orderStatus]}>
                                  {STATUS_MAP[orderStatus].statusItemLabel}
                              </StandardChip>
                          </LocalizedStrict>
                      ),
                      isElement: true,
                  },
              ]
            : []),
        {
            key: "time",
            label: (
                <LocalizedStrict id="meeting-service-order-drawer-delivery-time">
                    <span>Delivery time</span>
                </LocalizedStrict>
            ),
            value: deliveryTimeLabel,
        },
        {
            key: "contact",
            label: (
                <LocalizedStrict id="meeting-service-order-drawer-contact">
                    <span>Contact</span>
                </LocalizedStrict>
            ),
            value: (
                <Stack direction="row" alignItems="center" spacing=".5rem">
                    <Typography component="span" variant="body2" className={classes.detailValue}>
                        {contactName}
                    </Typography>
                    {hasContactInfo && (
                        <LocalizedStrict id="meeting-service-order-drawer-contact-info-button" attrs={{ title: true }}>
                            <Tooltip title="Show contact information">
                                <IconButton
                                    size="small"
                                    className={classes.contactInfoButton}
                                    onClick={() => setIsContactDialogOpen(true)}
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                </IconButton>
                            </Tooltip>
                        </LocalizedStrict>
                    )}
                </Stack>
            ),
            isElement: true,
        },
        {
            key: "covers",
            label: (
                <LocalizedStrict id="meeting-service-order-drawer-covers">
                    <span>Covers</span>
                </LocalizedStrict>
            ),
            value: (
                <LocalizedStrict id="meeting-service-order-drawer-covers-value" vars={{ count: sittings }}>
                    <span>{`${sittings} people`}</span>
                </LocalizedStrict>
            ),
        },
        {
            key: "type",
            label: (
                <LocalizedStrict id="meeting-service-order-drawer-delivery-type">
                    <span>Delivery type</span>
                </LocalizedStrict>
            ),
            value: deliveryTypeLabel,
        },
        ...(locationName
            ? [
                  {
                      key: "location",
                      label: (
                          <LocalizedStrict id="meeting-service-order-drawer-location">
                              <span>Delivery location</span>
                          </LocalizedStrict>
                      ),
                      value: locationName,
                  },
              ]
            : []),
    ]

    return (
        <>
            <DrawerDetailsCard>
                {detailRows.map((row) => (
                    <DrawerDetailRow key={row.key} label={row.label} value={row.value} isElement={row.isElement} />
                ))}
            </DrawerDetailsCard>

            {!!details.note && (
                <Box className={classes.noteCallout}>
                    <Stack direction="row" alignItems="center" spacing=".5rem" marginBottom=".25rem">
                        <FontAwesomeIcon icon={faComment} size="sm" className={classes.noteTitle} />
                        <LocalizedStrict id="meeting-service-order-drawer-order-note">
                            <Typography component="span" variant="caption" className={classes.noteTitle}>
                                Order note
                            </Typography>
                        </LocalizedStrict>
                    </Stack>
                    <ExpandableText text={details.note} className={classes.noteText} />
                </Box>
            )}

            <HostContactDetailsDialog
                isOpen={isContactDialogOpen}
                dialogTitle={
                    <LocalizedStrict id="meeting-service-order-drawer-contact-dialog-title">
                        <>Contact details</>
                    </LocalizedStrict>
                }
                host={
                    contact
                        ? {
                              name: contact.name ?? "",
                              email: contact.email,
                              phone: contact.phone,
                              businessPhone: contact.businessPhone,
                              mobilePhone: contact.mobilePhone,
                          }
                        : null
                }
                onClose={() => setIsContactDialogOpen(false)}
            />
        </>
    )
}
